import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {cn} from '../../lib/cn';
import {FieldErrorMessage} from '../../field-error-message';
import type {SelectOption} from '../../lib/selector-option';
import {countriesFilter} from '../_select-internals';
import {Select} from '../select';
import {Input} from '../input';
import {
  clearPhoneNumber,
  formatPhoneCodeOptionLabel,
  parsePhoneValueWithOptions,
  toPhoneValue,
} from './utils';
import type {PhoneInputOption, PhoneInputValue} from './types';

type PhoneInputHandle = {
  getCodeSearchValue: () => string;
  setCodeSearchValue: (value: string) => void;
  getNumberValue: () => string;
  setNumberValue: (value: string) => void;
  focus: () => void;
};

type PhoneInputSubfield = 'code' | 'number';

type PhoneInputProps = {
  options: PhoneInputOption[];
  value?: string | number | PhoneInputValue | null;
  onChange?: (value: PhoneInputValue, name?: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onFieldFocus?: (subfield: PhoneInputSubfield) => void;
  onFieldBlur?: (subfield: PhoneInputSubfield) => void;
  name?: string;
  codeName?: string;
  numberName?: string;
  label?: string;
  topLabel?: string;
  prefixLabel?: string;
  placeholder?: string;
  codePlaceholder?: string;
  disabled?: boolean;
  loading?: boolean;
  readOnly?: boolean;
  codeReadOnly?: boolean;
  optional?: boolean | string;
  tooltip?: React.ReactNode;
  error?: string;
  invalid?: boolean;
  className?: string;
  autoDetectCode?: boolean;
  searchable?: boolean;
  defaultCode?: string;
  defaultNumber?: string;
};

const PhoneInput = React.forwardRef<PhoneInputHandle, PhoneInputProps>(
  function PhoneInput(
    {
      options,
      value,
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      onFieldFocus,
      onFieldBlur,
      name,
      codeName,
      numberName,
      label,
      topLabel,
      prefixLabel,
      placeholder,
      codePlaceholder = '+00',
      disabled,
      loading,
      readOnly,
      codeReadOnly,
      optional,
      tooltip,
      error,
      invalid,
      className,
      autoDetectCode = true,
      searchable = true,
      defaultCode,
      defaultNumber,
    },
    ref,
  ) {
    const {t} = useTranslation();
    const groupLabelId = React.useId();
    const errorMsgId = React.useId();
    const numberInputRef = React.useRef<HTMLInputElement>(null);
    const [codeSearchValue, setCodeSearchValue] = React.useState('');

    const safeValue: PhoneInputValue = toPhoneValue(value, defaultCode, defaultNumber);
    const effectiveCode = safeValue.code || defaultCode || '';
    const resolvedLabel = label ?? '';
    const hasExternalError = Boolean(error);
    const isPrefixRequired =
      autoDetectCode && Boolean(safeValue.number) && !effectiveCode;
    const hasInvalidState = hasExternalError || Boolean(invalid) || isPrefixRequired;
    const errorMessage = error ?? (isPrefixRequired ? t('prefix_required') : undefined);
    const combinedValue =
      effectiveCode || safeValue.number ? `${effectiveCode}${safeValue.number}` : '';

    const codeOptions = React.useMemo<SelectOption[]>(
      () =>
        options.map(option => ({
          value: option.value,
          label: formatPhoneCodeOptionLabel(option),
          data: option.data,
          isDisabled: option.disabled,
        })),
      [options],
    );

    const selectedCodeOption = React.useMemo(
      () => codeOptions.find(option => option.value === effectiveCode) ?? null,
      [codeOptions, effectiveCode],
    );

    const parsePhoneValue = React.useMemo(
      () => parsePhoneValueWithOptions(options),
      [options],
    );

    const emitChange = (next: PhoneInputValue) => {
      onChange?.(next, name);
    };

    const handleCodeChange = (option: SelectOption | null) => {
      if (!option) return;
      emitChange({code: option.value, number: safeValue.number});
      if (!disabled && !readOnly) {
        setTimeout(() => numberInputRef.current?.focus(), 0);
      }
    };

    const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly || disabled) return;
      const rawValue = event.target.value;

      if (!autoDetectCode) {
        emitChange({code: effectiveCode, number: clearPhoneNumber(rawValue)});
        return;
      }

      const parsed = parsePhoneValue(rawValue);
      const cleanedNumber = clearPhoneNumber(parsed.number);

      if (parsed.code) {
        emitChange({code: parsed.code, number: cleanedNumber});
        return;
      }

      emitChange({code: effectiveCode, number: cleanedNumber});
    };

    const effectiveCodeRef = React.useRef(effectiveCode);
    const numberValueRef = React.useRef(safeValue.number);
    const codeSearchValueRef = React.useRef(codeSearchValue);
    const onChangeRef = React.useRef(onChange);
    const nameRef = React.useRef(name);
    effectiveCodeRef.current = effectiveCode;
    numberValueRef.current = safeValue.number;
    codeSearchValueRef.current = codeSearchValue;
    onChangeRef.current = onChange;
    nameRef.current = name;

    React.useImperativeHandle(
      ref,
      () => ({
        getCodeSearchValue: () => codeSearchValueRef.current,
        setCodeSearchValue: (next: string) => setCodeSearchValue(next),
        getNumberValue: () => numberValueRef.current,
        setNumberValue: (next: string) => {
          const cleanedNumber = clearPhoneNumber(next);
          onChangeRef.current?.(
            {code: effectiveCodeRef.current, number: cleanedNumber},
            nameRef.current,
          );
        },
        focus: () => numberInputRef.current?.focus(),
      }),
      [],
    );

    return (
      <div
        className={cn(
          'relative w-[var(--field-width,100%)] max-w-[var(--field-max-width,296px)]',
          disabled && 'cursor-not-allowed opacity-50',
          loading && 'cursor-progress',
          className,
        )}
      >
        {name && (
          <input type="hidden" name={name} value={combinedValue} disabled={disabled} />
        )}
        {codeName && (
          <input
            type="hidden"
            name={codeName}
            value={effectiveCode}
            disabled={disabled}
          />
        )}
        {numberName && (
          <input
            type="hidden"
            name={numberName}
            value={safeValue.number}
            disabled={disabled}
          />
        )}
        {topLabel && (
          <span
            id={groupLabelId}
            className="mb-2 block text-[length:var(--field-top-label-font-size)] font-[var(--field-top-label-font-weight)] text-[var(--chekin-color-brand-navy)]"
          >
            {topLabel}
          </span>
        )}
        <div
          role="group"
          aria-labelledby={topLabel ? groupLabelId : undefined}
          className="grid grid-cols-[96px_minmax(0,1fr)] gap-2"
        >
          <Select
            options={codeOptions}
            value={selectedCodeOption}
            onChange={handleCodeChange}
            label={prefixLabel}
            placeholder={codePlaceholder}
            disabled={disabled}
            readOnly={Boolean(readOnly) || Boolean(codeReadOnly)}
            loading={loading}
            invalid={hasInvalidState}
            hideErrorMessage
            searchPosition={searchable ? 'dropdown' : 'trigger'}
            searchable={searchable}
            filterOption={countriesFilter}
            clearable={false}
            getValueLabel={option => option.value}
            className="max-w-none w-auto"
            dropdownClassName="right-auto w-[280px]"
            inputValue={searchable ? codeSearchValue : undefined}
            onInputChange={setCodeSearchValue}
            onFocus={() => onFieldFocus?.('code')}
            onBlur={() => onFieldBlur?.('code')}
          />
          <Input
            ref={numberInputRef}
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            label={resolvedLabel}
            value={safeValue.number}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            loading={loading}
            invalid={hasInvalidState}
            tooltip={tooltip}
            aria-label={resolvedLabel || name}
            aria-describedby={errorMessage ? errorMsgId : undefined}
            onChange={handleNumberChange}
            onKeyDown={onKeyDown}
            onFocus={event => {
              onFocus?.(event);
              onFieldFocus?.('number');
            }}
            onBlur={event => {
              onBlur?.(event);
              onFieldBlur?.('number');
            }}
            renderErrorMessage={false}
            wrapperClassName="max-w-none w-auto"
            contentClassName={readOnly ? '!cursor-default' : undefined}
            inputClassName={readOnly ? '!cursor-default' : undefined}
          />
        </div>
        {!errorMessage && optional && (
          <span className="mt-[1px] block text-left text-[14px] font-medium text-[var(--chekin-color-gray-1)]">
            {typeof optional === 'string' ? optional : t('optional')}
          </span>
        )}
        {errorMessage && (
          <FieldErrorMessage
            id={errorMsgId}
            message={errorMessage}
            data-testid={name ? `${name}-error` : undefined}
            className="mt-[1px] text-[14px]"
          />
        )}
      </div>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';

export {PhoneInput};
export type {PhoneInputProps, PhoneInputHandle, PhoneInputSubfield};
